import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, Search, X, Loader2, ChevronDown, ChevronRight, Package, Trash2, MoveRight, Building2, Download, Scissors, Tag, AlertTriangle, Layers, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import * as api from '@/services/seasonalWmsApi';
import { SplitBoxDialog } from './SplitBoxDialog';
import { ReslugBoxDialog } from './ReslugBoxDialog';

const StatCard = ({ label, value, color, testid }) => (
  <div className="bg-white rounded-lg border border-[#E5DFD0] p-4" data-testid={testid}>
    <p className="text-xs text-[#5A5F6E]">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color || 'text-[#1A1F2E]'}`}>{value}</p>
  </div>
);

const SeasonalLocationStockReport = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ bins: [], total_bins: 0, total_boxes: 0, empty_bins: 0 });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [allBins, setAllBins] = useState([]);
  const [reloc, setReloc] = useState(null); // {box, current_location_id, customer}
  const [relocTarget, setRelocTarget] = useState('');
  const [busy, setBusy] = useState(false);
  const [cancel, setCancel] = useState(null); // {box, customer}
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [split, setSplit] = useState(null); // {box, slug} to split
  const [reslug, setReslug] = useState(null); // {box, knownSlugs}
  const [audit, setAudit] = useState(null); // multi-slug report {rows,total} | null
  const [auditBusy, setAuditBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set()); // box_numbers for bulk pooling
  const [pool, setPool] = useState(null); // common pool {boxes,total,total_qty} | null
  const [poolBusy, setPoolBusy] = useState(false);
  const [resPool, setResPool] = useState(null); // reserved box to release+pool {box} | null
  const [resPoolBusy, setResPoolBusy] = useState(false);

  const toggleSel = (bn) => setSelected((p) => { const n = new Set(p); n.has(bn) ? n.delete(bn) : n.add(bn); return n; });
  const clearSel = () => setSelected(new Set());

  const sendPool = async (boxNumbers) => {
    setPoolBusy(true);
    try {
      const r = await api.sendToPool(boxNumbers);
      toast.success(r.message);
      clearSel();
      await load(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to move to pool'); }
    finally { setPoolBusy(false); }
  };
  const openPool = async () => {
    setPoolBusy(true);
    try { setPool(await api.getCommonPool()); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load Common Pool'); }
    finally { setPoolBusy(false); }
  };
  const restorePool = async (bn) => {
    try {
      const r = await api.restoreFromPool([bn]);
      toast.success(r.message);
      await openPool();
      await load(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Restore failed'); }
  };
  const doReservedPool = async () => {
    if (!resPool?.box) return;
    setResPoolBusy(true);
    try {
      const r = await api.reservedToPool([resPool.box.box_number]);
      if ((r.pooled || []).length) toast.success(r.message);
      else toast.error((r.skipped?.[0]?.why) || 'Could not pool this box');
      setResPool(null);
      await load(search);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to pool reserved box'); }
    finally { setResPoolBusy(false); }
  };

  // All slugs seen in the report for a given customer (re-slug suggestions).
  const slugsForCustomer = useCallback((customer) => {
    const s = new Set();
    (data.bins || []).forEach((b) => (b.boxes || []).forEach((bx) => {
      if ((b.customer === customer || bx.customer === customer) && bx.slug) s.add(bx.slug);
    }));
    return [...s].sort();
  }, [data]);

  const openAudit = async () => {
    setAuditBusy(true);
    try {
      const r = await api.getMultiSlugSkus();
      setAudit(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load slug audit');
    } finally { setAuditBusy(false); }
  };

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const [rep, locs] = await Promise.all([api.getStockReport(q), api.getLocations('position')]);
      setData(rep);
      setAllBins(locs.locations || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load stock report');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(''); }, [load]);

  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openCancel = (box, customer) => { setCancel({ box, customer }); setCancelReason(''); setCancelNotes(''); };
  const doCancel = async () => {
    if (!cancelReason) { toast.error('Choose a cancellation reason'); return; }
    const reason = cancelNotes.trim() ? `${cancelReason} — ${cancelNotes.trim()}` : cancelReason;
    setCancelBusy(true);
    try {
      const r = await api.cancelBox(cancel.box.box_number, reason);
      toast.success(r.message);
      setCancel(null);
      await load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally { setCancelBusy(false); }
  };

  const openReloc = (box, customer, currentLoc) => { setReloc({ box, customer, currentLoc }); setRelocTarget(''); };
  const doRelocate = async () => {
    if (!relocTarget) { toast.error('Pick a destination bin'); return; }
    setBusy(true);
    try { const r = await api.relocateBox(reloc.box.box_number, relocTarget); toast.success(r.message); setReloc(null); await load(search); }
    catch (e) { toast.error(e.response?.data?.detail || 'Relocate failed'); }
    finally { setBusy(false); }
  };

  // eligible destination bins for the relocate dialog (same customer or empty, not full, not current)
  const relocOptions = reloc ? allBins.filter((b) =>
    b.location_id !== reloc.currentLoc && b.status !== 'full' &&
    (!b.current_customer || b.current_customer === reloc.customer) &&
    !(b.capacity_boxes && (b.box_count || 0) >= b.capacity_boxes)
  ) : [];

  // Export the CURRENT (filtered) report as CSV — ONE ROW PER SKU.
  const exportCsv = () => {
    const rows = [];
    (data.bins || []).forEach((b) => (b.boxes || []).forEach((box) => {
      const items = (box.items && box.items.length) ? box.items : [{}];
      items.forEach((it) => rows.push([
        b.footprint || '', b.customer || '', box.box_number || '', box.slug || '',
        box.pick_slip_number || '',
        it.barcode || '',
        it.design_name || '', it.shade || '', it.size || '', it.description || '',
        it.qty ?? '', box.status || '',
      ]));
    }));
    if (rows.length === 0) { toast.error('Nothing to export'); return; }
    const headers = ['Bin (Footprint)', 'Customer', 'Box Number', 'Slug', 'Pick Slip',
      'Barcode', 'Design Name', 'Shade', 'Size', 'Description', 'Qty', 'Box Status'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seasonal_location_stock_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} SKU row(s)`);
  };

  // Export the Common Pool as CSV — ONE ROW PER BOX × SKU, with the box's location.
  const exportPoolCsv = () => {
    const rows = [];
    (pool?.boxes || []).forEach((bx) => (bx.items || []).forEach((it) => rows.push([
      bx.footprint || '', bx.box_number || '', bx.external_box_no || '', it.barcode || '', it.design_name || '', it.shade || '',
      it.size || '', it.description || '', Number(it.qty || 0), bx.pool_orig_customer || '',
    ])));
    rows.sort((a, b) => String(a[0]).localeCompare(String(b[0])) || String(a[1]).localeCompare(String(b[1])));
    if (rows.length === 0) { toast.error('Common Pool is empty'); return; }
    const headers = ['Location', 'Box No', 'Small Box Code', 'SKU / Barcode', 'Design Name', 'Shade', 'Size', 'Description', 'Qty', 'Pooled From'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `common_pool_stock_${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '')}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} SKU row(s)`);
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]" data-testid="seasonal-stock-report-page">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/wms/seasonal-bundle')} className="text-[#F9FAFB] hover:bg-white/10 mr-3" data-testid="ssr-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Boxes className="w-6 h-6 mr-3 text-fuchsia-400" />
          <div>
            <h1 className="text-xl font-bold font-display">Location Stock Report</h1>
            <p className="text-sm text-[#E8DCC8]">Bin-wise seasonal stock — cancel or relocate bundles</p>
          </div>
          <Button onClick={openPool} disabled={poolBusy}
            className="ml-auto bg-teal-600 hover:bg-teal-700 text-white" data-testid="ssr-common-pool-btn">
            {poolBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />} Common Pool
          </Button>
          <Button onClick={openAudit} disabled={auditBusy}
            className="ml-3 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ssr-slug-audit-btn">
            {auditBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />} Slug Audit
          </Button>
          <Button onClick={exportCsv} disabled={loading || !(data.bins || []).length}
            className="ml-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="ssr-export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-2">
            <CardContent className="pt-5">
              <label className="block text-xs text-[#5A5F6E] mb-1">Filter</label>
              <div className="flex gap-2">
                <Input value={search} onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') load(search); }}
                  placeholder="Search bin, customer, box, slug…" className="flex-1" data-testid="ssr-search-input" />
                <Button onClick={() => load(search)} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white" data-testid="ssr-search-btn"><Search className="w-4 h-4" /></Button>
                <Button variant="outline" onClick={() => { setSearch(''); load(''); }} className="border-[#E5DFD0]" data-testid="ssr-clear-btn"><X className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
          <StatCard label="Occupied Bins" value={data.total_bins} color="text-indigo-700" testid="ssr-stat-bins" />
          <StatCard label="Boxes in Storage" value={data.total_boxes} color="text-amber-700" testid="ssr-stat-boxes" />
        </div>

        <p className="text-xs text-[#8A8F9E]" data-testid="ssr-empty-summary">{data.empty_bins} empty bin(s) available.</p>

        {selected.size > 0 && (
          <div className="sticky top-2 z-10 flex items-center gap-3 bg-teal-600 text-white rounded-lg px-4 py-2.5 shadow-lg" data-testid="ssr-bulk-bar">
            <span className="text-sm font-semibold">{selected.size} box(es) selected</span>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" onClick={clearSel} data-testid="ssr-bulk-clear">Clear</Button>
            <Button size="sm" className="bg-white text-teal-700 hover:bg-teal-50" disabled={poolBusy} onClick={() => sendPool([...selected])} data-testid="ssr-bulk-send-pool">
              {poolBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />} Send {selected.size} to Pool
            </Button>
          </div>
        )}

        {loading ? (
          <p className="text-center text-[#5A5F6E] py-10"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</p>
        ) : data.bins.length === 0 ? (
          <div className="text-center text-[#5A5F6E] py-10 bg-white rounded-xl border border-[#E5DFD0]" data-testid="ssr-empty">No stock in bins.</div>
        ) : (
          <div className="space-y-3" data-testid="ssr-bin-list">
            {(data.bins || []).map((b) => {
              const open = expanded.has(b.location_id);
              return (
                <Card key={b.location_id} data-testid={`ssr-bin-${b.location_id}`}>
                  <button onClick={() => toggle(b.location_id)} className="w-full flex items-center gap-3 px-4 py-3 text-left" data-testid={`ssr-bin-toggle-${b.location_id}`}>
                    {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Building2 className="w-4 h-4 text-fuchsia-600" />
                    <span className="font-mono text-sm font-semibold">{b.footprint}</span>
                    <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[11px]">{b.customer}</Badge>
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px]">{b.box_count}{b.capacity_boxes ? `/${b.capacity_boxes}` : ''} boxes</Badge>
                    {b.bin_status === 'full' && <Badge className="bg-red-100 text-red-700 border-0 text-[11px]">FULL</Badge>}
                  </button>
                  {open && (
                    <CardContent className="pt-0">
                      <div className="divide-y border-t">
                        {(b.boxes || []).map((box) => (
                          <div key={box.inv_id} className="py-2.5" data-testid={`ssr-box-${box.box_number}`}>
                            <div className="flex items-center gap-3">
                              {box.status === 'stored' && (
                                <input type="checkbox" className="w-4 h-4 accent-teal-600 shrink-0 cursor-pointer"
                                  checked={selected.has(box.box_number)} onChange={() => toggleSel(box.box_number)}
                                  data-testid={`ssr-select-${box.box_number}`} />
                              )}
                              <Package className="w-4 h-4 text-gray-400 shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-mono text-sm">{box.box_number}</p>
                                <p className="text-xs text-[#8A8F9E]">Slug {box.slug || '—'} · {box.total_qty} pcs · {box.status}</p>
                              </div>
                              {box.status === 'stored' ? (
                                <div className="flex gap-2 shrink-0">
                                  <Button size="sm" variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50" onClick={() => sendPool([box.box_number])} data-testid={`ssr-pool-${box.box_number}`}>
                                    <Layers className="w-4 h-4 mr-1" /> Pool
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50" onClick={() => setSplit({ ...box })} data-testid={`ssr-split-${box.box_number}`}>
                                    <Scissors className="w-4 h-4 mr-1" /> Split
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50" onClick={() => setReslug({ box: { ...box }, knownSlugs: slugsForCustomer(b.customer) })} data-testid={`ssr-reslug-${box.box_number}`}>
                                    <Tag className="w-4 h-4 mr-1" /> Re-slug
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50" onClick={() => openReloc(box, b.customer, b.location_id)} data-testid={`ssr-relocate-${box.box_number}`}>
                                    <MoveRight className="w-4 h-4 mr-1" /> Relocate
                                  </Button>
                                  <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => openCancel(box, b.customer)} data-testid={`ssr-cancel-${box.box_number}`}>
                                    <Trash2 className="w-4 h-4 mr-1" /> Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 shrink-0">
                                  <Badge className="bg-gray-100 text-gray-500 border-0 text-[11px]">{box.status} (in pick slip)</Badge>
                                  {box.status === 'reserved' && (
                                    <Button size="sm" variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50"
                                      onClick={() => setResPool({ box: { ...box }, customer: b.customer })}
                                      data-testid={`ssr-reserved-pool-${box.box_number}`}>
                                      <Layers className="w-4 h-4 mr-1" /> Pool
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                            {(box.items && box.items.length > 0) ? (
                              <div className="mt-2 ml-7 overflow-x-auto rounded-md border border-[#EFE9DC]">
                                <table className="w-full text-xs" data-testid={`ssr-box-skus-${box.box_number}`}>
                                  <thead className="bg-[#F5F1E8] text-[#5A5F6E]">
                                    <tr>
                                      <th className="text-left px-2 py-1 font-medium">Barcode</th>
                                      <th className="text-left px-2 py-1 font-medium">Design Name</th>
                                      <th className="text-left px-2 py-1 font-medium">Shade</th>
                                      <th className="text-left px-2 py-1 font-medium">Size</th>
                                      <th className="text-left px-2 py-1 font-medium">Description</th>
                                      <th className="text-right px-2 py-1 font-medium">Qty</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-[#F0EADD] bg-white">
                                    {box.items.map((it, i) => (
                                      <tr key={`${box.inv_id}-${i}`} data-testid={`ssr-sku-${box.box_number}-${i}`}>
                                        <td className="px-2 py-1 font-mono text-[11px]" data-testid={`ssr-sku-barcode-${box.box_number}-${i}`}>{it.barcode || '—'}</td>
                                        <td className="px-2 py-1">{it.design_name || '—'}</td>
                                        <td className="px-2 py-1">{it.shade || '—'}</td>
                                        <td className="px-2 py-1">{it.size || '—'}</td>
                                        <td className="px-2 py-1">{it.description || '—'}</td>
                                        <td className="px-2 py-1 text-right tabular-nums">{it.qty ?? 0}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p className="mt-1 ml-7 text-[11px] text-[#B0966A]" data-testid={`ssr-box-noskus-${box.box_number}`}>
                                No SKU breakdown available for this box.
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <Dialog open={!!reloc} onOpenChange={(o) => !o && setReloc(null)}>
        <DialogContent data-testid="ssr-relocate-dialog">
          <DialogHeader><DialogTitle>Relocate {reloc?.box?.box_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#5A5F6E]">Customer: <b>{reloc?.customer}</b> · one customer per bin is enforced.</p>
            <div>
              <label className="text-xs text-[#5A5F6E]">Destination bin (empty or same customer)</label>
              <Select value={relocTarget} onValueChange={setRelocTarget} data-testid="ssr-relocate-select-root">
                <SelectTrigger data-testid="ssr-relocate-select"><SelectValue placeholder="Choose a bin…" /></SelectTrigger>
                <SelectContent>
                  {(relocOptions || []).map((b) => (
                    <SelectItem key={b.location_id} value={b.location_id} data-testid={`ssr-relocate-opt-${b.location_id}`}>
                      {b.footprint} · {b.box_count || 0}{b.capacity_boxes ? `/${b.capacity_boxes}` : ''}{b.current_customer ? ` · ${b.current_customer}` : ' · empty'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {relocOptions.length === 0 && <p className="text-xs text-red-500 mt-1">No eligible bin — add/empty a bin first.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReloc(null)} data-testid="ssr-relocate-cancel">Cancel</Button>
            <Button onClick={doRelocate} disabled={busy || !relocTarget} className="bg-sky-600 hover:bg-sky-700" data-testid="ssr-relocate-confirm">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cancel} onOpenChange={(o) => !o && setCancel(null)}>
        <DialogContent data-testid="ssr-cancel-dialog">
          <DialogHeader><DialogTitle>Cancel Box {cancel?.box?.box_number}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="ssr-cancel-note">
              This breaks the box down. Its {cancel?.box?.total_qty || 0} piece(s) return to the
              <b> FG Cancelled Slip Queue</b> under <b>{cancel?.box?.pick_slip_number}</b> and must be
              scanned back into FG racks via the mobile put-away flow. The box is removed from seasonal storage.
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Cancellation reason <span className="text-red-500">*</span></label>
              <Select value={cancelReason} onValueChange={setCancelReason} data-testid="ssr-cancel-reason-select-root">
                <SelectTrigger data-testid="ssr-cancel-reason-select"><SelectValue placeholder="Choose a reason…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Damaged" data-testid="ssr-cancel-reason-damaged">Damaged</SelectItem>
                  <SelectItem value="Wrong customer" data-testid="ssr-cancel-reason-wrong-customer">Wrong customer</SelectItem>
                  <SelectItem value="Order cancelled" data-testid="ssr-cancel-reason-order-cancelled">Order cancelled</SelectItem>
                  <SelectItem value="Re-pack needed" data-testid="ssr-cancel-reason-repack">Re-pack needed</SelectItem>
                  <SelectItem value="Other" data-testid="ssr-cancel-reason-other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-[#5A5F6E]">Notes (optional)</label>
              <Input value={cancelNotes} onChange={(e) => setCancelNotes(e.target.value)}
                placeholder="Add any detail…" data-testid="ssr-cancel-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancel(null)} data-testid="ssr-cancel-dialog-back">Back</Button>
            <Button onClick={doCancel} disabled={cancelBusy || !cancelReason} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ssr-cancel-confirm">
              {cancelBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Cancel Box
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resPool} onOpenChange={(o) => !o && setResPool(null)}>
        <DialogContent data-testid="ssr-reserved-pool-dialog">
          <DialogHeader><DialogTitle>Move {resPool?.box?.box_number} to Common Pool</DialogTitle>
            <DialogDescription className="sr-only">Release this reserved box from its pick slip and move it to the Common Pool.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded-lg bg-teal-50 border border-teal-200 p-3 text-xs text-teal-800" data-testid="ssr-reserved-pool-note">
              This box is <b>reserved</b> under slug <b>{resPool?.box?.slug || '—'}</b> in pick slip
              <b> {resPool?.box?.pick_slip_number || '—'}</b>. Pooling it will <b>release it from that pick slip</b>
              {' '}(the DC-tag is reversed and it's pulled from any master carton), then move its
              {' '}{resPool?.box?.total_qty || 0} piece(s) into the <b>Common Pool</b> as generic stock
              {' '}(customer + slug untagged). The box stays in its bin.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResPool(null)} data-testid="ssr-reserved-pool-back">Back</Button>
            <Button onClick={doReservedPool} disabled={resPoolBusy} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="ssr-reserved-pool-confirm">
              {resPoolBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />} Move to Pool
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {split && (
        <SplitBoxDialog
          box={split}
          onClose={() => setSplit(null)}
          onDone={() => { setSplit(null); load(search); }}
        />
      )}

      {reslug && (
        <ReslugBoxDialog
          box={reslug.box}
          knownSlugs={reslug.knownSlugs}
          onClose={() => setReslug(null)}
          onDone={() => { setReslug(null); load(search); }}
        />
      )}

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent className="max-w-2xl" data-testid="ssr-slug-audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-indigo-600" /> Multi-slug SKU audit
            </DialogTitle>
            <DialogDescription>SKUs sitting under more than one slug for the same customer.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-[#5A5F6E]">
              SKUs sitting under more than one slug for the same customer — the usual sign of a
              Halnode↔WMS slug mismatch. Fix them with the <b>Re-slug</b> button on the box in the list.
            </p>
            {!audit || audit.total === 0 ? (
              <div className="text-center text-[#2D5F4F] py-6 text-sm" data-testid="ssr-slug-audit-empty">
                No multi-slug SKUs found — everything is aligned.
              </div>
            ) : (
              <div className="space-y-2" data-testid="ssr-slug-audit-list">
                {audit.rows.map((r, i) => (
                  <div key={`${r.customer}-${r.barcode}-${i}`} className="rounded-md border border-[#EFE9DC] p-3" data-testid={`ssr-slug-audit-row-${i}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-indigo-100 text-indigo-700 border-0 text-[11px]">{r.customer}</Badge>
                      <span className="font-mono text-xs font-semibold">{r.barcode}</span>
                      <span className="text-xs text-[#5A5F6E]">{r.design_name || r.description || ''} · {r.shade || '—'} · {r.size || '—'}</span>
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[11px] ml-auto">{r.total_qty} pcs · {r.slug_count} slugs</Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {r.slugs.map((s) => (
                        <div key={s.slug} className="text-[11px] bg-[#FAF7F0] border border-[#EFE9DC] rounded px-2 py-1">
                          <b>{s.slug}</b> · {s.qty} pcs
                          <span className="text-[#8A8F9E]"> · {(s.box_numbers || []).join(', ')}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAudit(null)} data-testid="ssr-slug-audit-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pool} onOpenChange={(o) => !o && setPool(null)}>
        <DialogContent className="max-w-2xl" data-testid="ssr-common-pool-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-teal-600" /> Common Pool
              {pool && <Badge className="bg-teal-100 text-teal-700 border-0 text-[11px]">{pool.total} box · {pool.total_qty} pcs</Badge>}
              {pool && pool.total > 0 && (
                <Button size="sm" variant="outline"
                  className="ml-auto mr-6 border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50"
                  onClick={exportPoolCsv} data-testid="ssr-pool-export-csv-btn">
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              )}
            </DialogTitle>
            <DialogDescription>Generic untagged bundles available for any customer's slip.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto">
            <p className="text-xs text-[#5A5F6E]">
              Generic (untagged) bundles available for any customer's seasonal slip. Each stays in its bin.
              Use <b>Restore</b> to put a box back under its original customer + slug.
            </p>
            {!pool || pool.total === 0 ? (
              <div className="text-center text-[#5A5F6E] py-6 text-sm" data-testid="ssr-common-pool-empty">The Common Pool is empty.</div>
            ) : (
              <div className="space-y-2" data-testid="ssr-common-pool-list">
                {pool.boxes.map((bx) => (
                  <div key={bx.inv_id} className="rounded-md border border-[#EFE9DC] p-3" data-testid={`ssr-pool-box-${bx.box_number}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold">{bx.box_number}</span>
                      {bx.external_box_no && (
                        <Badge className="bg-fuchsia-100 text-fuchsia-700 border-0 font-mono text-[11px]" title="Small-box code (pre-pasted BUN sticker)"
                          data-testid={`ssr-pool-box-code-${bx.box_number}`}>{bx.external_box_no}</Badge>
                      )}
                      <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">{bx.footprint || 'no bin'}</Badge>
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{bx.total_qty} pcs</Badge>
                      {bx.pool_orig_customer && <span className="text-[11px] text-[#8A8F9E]">was: {bx.pool_orig_customer}{bx.pool_orig_slug ? ` / ${bx.pool_orig_slug}` : ''}</span>}
                      <Button size="sm" variant="outline" className="ml-auto border-sky-200 text-sky-700 hover:bg-sky-50" onClick={() => restorePool(bx.box_number)} data-testid={`ssr-pool-restore-${bx.box_number}`}>
                        <Undo2 className="w-4 h-4 mr-1" /> Restore
                      </Button>
                    </div>
                    {(bx.items || []).length > 0 && (
                      <div className="mt-1.5 text-[11px] text-[#5A5F6E] flex flex-wrap gap-x-3 gap-y-0.5">
                        {bx.items.map((it, i) => (
                          <span key={i} className="font-mono">{it.barcode} · {it.shade || ''} {it.size || ''} · {it.qty}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPool(null)} data-testid="ssr-common-pool-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SeasonalLocationStockReport;
